import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

// Logo image URL from user
const LOGO_URL = "https://customer-assets.emergentagent.com/job_guest-experience-30/artifacts/yfd9yj4z_pixlr-image-generator-699c527fa82fb7194b38636b%20%281%29.png";

const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: t('nav.howItWorks'), href: '#come-funziona' },
    { name: t('nav.pricing'), href: '#prezzi' },
    { name: t('nav.dimensions'), href: '#dimensioni' },
    { name: t('nav.security'), href: '#sicurezza' },
    { name: t('nav.location'), href: '#posizione' },
    { name: t('nav.contact'), href: '#contatti' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10'
            : 'bg-transparent'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center group"
              data-testid="navbar-logo"
            >
              <img 
                src={LOGO_URL} 
                alt="Unlock Bags" 
                className="h-12 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-gray-400 hover:text-[#FFCC00] text-sm font-medium transition-colors duration-200"
                  data-testid={`nav-${link.href.replace('#', '')}`}
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Language Switcher & CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSwitcher />
              <a
                href="#prezzi"
                onClick={(e) => scrollToSection(e, '#prezzi')}
                data-testid="navbar-book-cta"
                className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide text-sm hover:bg-[#E6B800] px-6 py-3 transition-all duration-200"
              >
                {t('nav.bookNow')}
              </a>
            </div>

            {/* Mobile: Language Switcher + Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              <LanguageSwitcher />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-white"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-[#0A0A0A]" />
            <nav className="relative pt-24 px-6">
              <div className="flex flex-col gap-6">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-white text-2xl font-bold hover:text-[#FFCC00] transition-colors"
                    data-testid={`mobile-nav-${link.href.replace('#', '')}`}
                  >
                    {link.name}
                  </motion.a>
                ))}
                <motion.a
                  href="#prezzi"
                  onClick={(e) => scrollToSection(e, '#prezzi')}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.1 }}
                  className="inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide text-lg hover:bg-[#E6B800] px-8 py-4 mt-4 transition-all duration-200"
                  data-testid="mobile-book-cta"
                >
                  {t('nav.bookNow')}
                </motion.a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
